import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { sort } from 'fast-sort'
import { getTruncatedString } from '../../../utils/string'
import reactSelectBaseStyles from '../../../configuration/ReactSelect'
import {
  MasterCourseSelectOption,
  MasterCourseSelectSingleValue
} from './MasterCourseSelectOption'
import useLexicon from '../../../hooks/useLexicon'
import { useTheme } from 'styled-components'
import { ClearButton, MCSelectWrapper } from '../_Styled'
import { getTint } from '../../../utils/color'

const MasterCourseSelect = ({
  selected,
  selectedIsAcademicYear,
  masterCourses,
  academicYears,
  onMasterCourseSelect,
  includeAcademicYears,
  separateUpcomingJourneys,
  width
}) => {
  const lexicon = useLexicon()
  const theme = useTheme()
  const [options, setOptions] = useState([])
  // const [selectedCourse, setSelectedCourse] = useState()

  const styles = Object.assign({}, reactSelectBaseStyles)

  styles.container = provided => {
    return {
      ...reactSelectBaseStyles.container(provided),
      minWidth: 'fit-content',
      width: width ?? reactSelectBaseStyles.container(provided).width
    }
  }

  styles.valueContainer = () => {
    return {
      // there's some weird, hidden overflow below the valueContainer that is listening for clicks and opening the select
      pointerEvents: 'none',
      height: '44px',
      padding: '0 !important'
    }
  }
  styles.control = provided => {
    return {
      ...provided,
      border: theme?.selectBorder,
      cursor: 'pointer',
      height: '46px',
      width: '100%',
      padding: '2px 0',
      boxShadow: 'none',
      zIndex: '3',
      borderRadius: '5px',
      background: theme?.selectBackground,
      minWidth: 'fit-content',
      flexWrap: 'none'
    }
  }
  styles.option = provided => {
    return {
      ...provided,
      padding: '0 !important',
      overflow: 'hidden',
      borderRadius: '4px',
      background: `${theme?.selectBackground} !important`
    }
  }
  styles.menu = provided => {
    return {
      ...provided,
      zIndex: '100',
      background: theme?.selectBackground
    }
  }

  const allActiveCoursesOption = {
    value: -1,
    label: `All Active ${separateUpcomingJourneys ? lexicon.journey : lexicon.course}s`,
    id: -1,
    name: '',
    color: '',
    isAcademicYear: false
  }

  const upcomingJourneysOption = {
    value: -2,
    label: `All Upcoming ${lexicon.journey}s`,
    id: -2,
    name: '',
    color: '',
    isAcademicYear: false
  }

  const getOption = masterCourse => ({
    active: masterCourse.active,
    future: masterCourse.future,
    name: masterCourse.name,
    value: masterCourse.id,
    label: getTruncatedString(masterCourse.name, 70),
    color:
      theme?.style === 'dark'
        ? getTint(masterCourse.color, 0.3)
        : `#${masterCourse.color}`,
    gradeLevel: masterCourse.gradeLevel,
    gradeSort: masterCourse.gradeLevel.sort,
    nonQuestCourseSort: masterCourse.non_quest_course,
    isAcademicYear: false
  })

  const getAYOption = academicYear => ({
    name: academicYear.name,
    value: academicYear.id,
    label: `${getTruncatedString(academicYear.name, 70)} ${lexicon.course}s`,
    isAcademicYear: true
  })

  const getOptions = () => {
    const sortFields = ['active', 'future', 'nonQuestCourseSort', 'gradeSort', 'name']

    return academicYears.map(ay => {
      const ayOptions = masterCourses
        .filter(mc => mc.academic_year_id === ay.id)
        .map(getOption)

      const sorted = sort(ayOptions).by([
        { desc: o => o.active },
        { desc: o => o.future },
        { asc:  o => o.nonQuestCourseSort },
        { asc:  o => o.gradeSort },
        { asc:  o => o.name }
      ])

      return {
        label: ay.name,
        options: includeAcademicYears && sorted.length > 0
          ? [getAYOption(ay), ...sorted]
          : sorted
      }
    })
  }

  useEffect(() => {
    const opts = getOptions()
    if (!includeAcademicYears) {
      if (separateUpcomingJourneys) opts.unshift(upcomingJourneysOption)
      opts.unshift(allActiveCoursesOption)
    }
    setOptions(opts)
  }, [includeAcademicYears])

  // find value before options are generated because academic year grouping/nesting makes that complicated
  const value =
    selectedIsAcademicYear
      ? getAYOption(academicYears.find(ay => ay.id === selected) ?? academicYears[0])
      : selected === -1
        ? allActiveCoursesOption
        : selected === -2 && separateUpcomingJourneys
          ? upcomingJourneysOption
          : getOption(masterCourses.find(mc => mc.id === selected))

  const handleChange = selectOption => {
    // setSelectedCourse({ selectedCourse: selectOption })
    if (includeAcademicYears) {
      onMasterCourseSelect(selectOption.value, selectOption.isAcademicYear)
    } else {
      onMasterCourseSelect(selectOption.value)
    }
  }

  const reset = () => {
    if (includeAcademicYears) {
      const firstAY = options.find(o => !!o.options && o.options.length > 0)
      onMasterCourseSelect(firstAY ? firstAY.options[0].value : null, true)
    } else {
      onMasterCourseSelect(-1)
    }
  }

  return (
    <MCSelectWrapper>
      <Select
        autoFocus
        isClearable={false}
        isSearchable={false}
        options={options}
        value={value}
        onChange={handleChange}
        styles={styles}
        userTheme={theme}
        components={{
          Option: MasterCourseSelectOption,
          SingleValue: MasterCourseSelectSingleValue
        }}
      />
      <ClearButton>
        {value.value > 0 && !value.isAcademicYear && (
          <>
            <div className="clear" onClick={reset} />
            <div className="line" />
          </>
        )}
      </ClearButton>
    </MCSelectWrapper>
  )
}

MasterCourseSelect.propTypes = {
  onMasterCourseSelect: PropTypes.func.isRequired,
  masterCourses: PropTypes.array.isRequired,
  selected: PropTypes.number,
  selectedIsAcademicYear: PropTypes.bool,
  academicYears: PropTypes.array,
  includeAcademicYears: PropTypes.bool,
  separateUpcomingJourneys: PropTypes.bool
}

export default MasterCourseSelect
